import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[9px] text-[13px] font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Black, not the brand indigo: the colour stays for what it marks —
        // selection, links, the outbound side of a thread — and the buttons
        // read as one family across every screen.
        default: "bg-ink-900 text-white hover:bg-ink-900/90 dark:bg-white dark:text-background dark:hover:bg-white/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-background text-foreground hover:bg-black/[0.03] dark:hover:bg-white/[0.06]",
        soft: "border border-primary-soft-border bg-primary-soft text-primary-dim hover:bg-primary-soft/70",
        secondary: "bg-black/5 text-ink-900 font-medium hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/[0.16]",
        ghost: "text-mute font-medium hover:bg-black/[0.05] hover:text-ink-900 dark:hover:bg-white/[0.08]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // Explicit pixels, not rem: the root font is 87.5%, so a rem scale lands
      // 12.5% under what the kit specifies and the controls read cramped.
      size: {
        default: "h-[32px] px-4",
        sm: "h-[26px] px-2.5 text-[11.5px]",
        lg: "h-[36px] px-5 text-[13px]",
        icon: "h-[26px] w-[26px] px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
